import React, { useEffect, useState, useCallback, useMemo, Suspense, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import CategoryPromotionManager from "../../components/admin/CategoryPromotionManager";
import GroupAnnotatorMultiPriceFetcher from "../../components/admin/GroupAnnotatorMultiPriceFetcher";
import ExtActionManager from "../../components/admin/ExtActionManager";
const GroupAnnotatorDetails = React.lazy(() => import("./GroupAnnotatorDetails"));
const GroupAnnotatorProductIdentifier = React.lazy(() => import("./GroupAnnotatorProductIdentifier"));
const GroupAnnotatorPriceFirstProduct = React.lazy(() => import("./GroupAnnotatorPriceFirstProduct"));
const GroupAnnotatorMultiPriceFirstProduct = React.lazy(() => import("./GroupAnnotatorMultiPriceFirstProduct"));
const GroupAnnotatorGroupProduct = React.lazy(() => import("./GroupAnnotatorGroupProduct"));
const GroupAnnotatorBaseProduct = React.lazy(() => import("./GroupAnnotatorBaseProduct"));
const ProductOfferGroupManager = React.lazy(() => import("./ProductOfferGroupManager"));
const GroupAnnotatorAutoloader = React.lazy(() => import("./GroupAnnotatorAutoloader"));

type Slice = {
    id: number;
    image_id: number;
    slice_url?: string;
    url?: string;
    image_url?: string
};

interface Product {
    id: number;
    name: string;
    category_id: number;
    category: string;
    brand_id: number | null;
    brand_name: string | null;
    ean_code: string | null;
}

interface Offer {
    id: number;
    currency: string;
    min_quantity: number;
    x: number;
    y: number;
    width: number | null;
    height: number | null;
    prices?: Array<{
        id: number;
        price_type: {
            id: number;
            name: string;
            code: string;
        };
        value: number;
        starts_at?: string | null;
        ends_at?: string | null;
    }>;
    packaging?: {
        id: number;
        name: string;
        size_value: string | null;
        size_unit: string | null;
        multiplier: number;
    } | null;
    family_group_id?: string | null;
    group_group_id?: string | null;
}

const pageStyle: React.CSSProperties = {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#111",
    overflow: "auto",
};

const paginatorBarStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px 20px",
    background: "rgba(255,255,255,0.02)",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    gap: 12,
    flexShrink: 0,
};

const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 20,
    width: "100%",
    maxWidth: 2500,
    padding: 20,
    boxSizing: "border-box",
    alignContent: "start",
    margin: "0 auto",
};

const columnBase: React.CSSProperties = {
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    minHeight: 420,
};

const firstColumnStyle: React.CSSProperties = {
    ...columnBase,
    flexDirection: "column",
    padding: 0,
    minHeight: 800,
};

const halfColumnStyle: React.CSSProperties = {
    ...columnBase,
    position: "relative",
    flex: 1,
    minHeight: "calc(420px / 2 - 10px)",
};

const imageStyle: React.CSSProperties = {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
};

const placeholderStyle: React.CSSProperties = {
    width: "90%",
    height: "90%",
    border: "2px dashed rgba(255,255,255,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bbb",
    padding: 12,
    textAlign: "center",
};

const labelStyle: React.CSSProperties = { position: "absolute", top: 8, left: 12, color: "#ddd", fontSize: 13 };


const imageWrapperStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
};

const paginatorStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    zIndex: 50,
};

const paginatorButtonStyle: React.CSSProperties = {
    padding: "6px 12px",
    backgroundColor: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 4,
    color: "#ddd",
    cursor: "pointer",
    fontSize: 12,
    transition: "all 0.2s",
};

const paginatorButtonHoverStyle: React.CSSProperties = {
    ...paginatorButtonStyle,
    backgroundColor: "rgba(255,255,255,0.2)",
};

const paginatorInputStyle: React.CSSProperties = {
    padding: "4px 8px",
    width: 50,
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 4,
    color: "#ddd",
    fontSize: 12,
    textAlign: "center",
};

const paginatorNumbersContainerStyle: React.CSSProperties = {
    display: "flex",
    gap: 4,
    alignItems: "center",
    flexWrap: "wrap",
};

const paginatorNumberStyle: React.CSSProperties = {
    padding: "4px 8px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    color: "#999",
    cursor: "pointer",
    fontSize: 12,
    minWidth: 28,
    textAlign: "center",
    transition: "all 0.2s",
};

const paginatorNumberActiveStyle: React.CSSProperties = {
    ...paginatorNumberStyle,
    backgroundColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.4)",
    color: "#ef4444",
    fontWeight: 600,
};

const paginatorEllipsisStyle: React.CSSProperties = {
    color: "#666",
    fontSize: 12,
    padding: "4px 4px",
};

const toggleStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 8px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 12,
    color: "#ddd",
    transition: "all 0.2s",
};

const toggleActiveStyle: React.CSSProperties = {
    ...toggleStyle,
    backgroundColor: "rgba(239, 68, 68, 0.15)",
    borderColor: "rgba(239, 68, 68, 0.4)",
    color: "#ef4444",
};

const toggleSwitchStyle: React.CSSProperties = {
    display: "inline-block",
    width: 24,
    height: 14,
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: 7,
    position: "relative",
    transition: "background-color 0.2s",
};

const toggleSwitchActiveStyle: React.CSSProperties = {
    ...toggleSwitchStyle,
    backgroundColor: "#ef4444",
};

const toggleDotStyle: React.CSSProperties = {
    position: "absolute",
    top: 2,
    left: 2,
    width: 10,
    height: 10,
    backgroundColor: "white",
    borderRadius: "50%",
    transition: "left 0.2s",
};

const toggleDotActiveStyle: React.CSSProperties = {
    ...toggleDotStyle,
    left: 12,
};

const accordionPanelStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    minHeight: "auto",
    flex: "0 0 auto",
    overflow: "hidden",
    transition: "flex 0.3s ease-in-out, min-height 0.3s ease-in-out",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const accordionPanelOpenStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    minHeight: "auto",
    flex: "0 0 auto",
    overflow: "visible",
    transition: "all 0.3s ease-in-out",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const accordionHeaderStyle: React.CSSProperties = {
    padding: "12px 16px",
    backgroundColor: "rgba(255,255,255,0.04)",
    border: "none",
    color: "#ddd",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "background-color 0.2s",
    userSelect: "none",
    width: "100%",
    textAlign: "left",
    flexShrink: 0,
};

const accordionContentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    padding: "12px 8px",
    overflow: "visible",
};

const accordionContainerStyle: React.CSSProperties = {
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 6,
    display: "flex",
    flexDirection: "column",
    padding: 0,
    overflow: "visible",
};

const timerContainerStyle: React.CSSProperties = {
    background: "#0a0a0a",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 6,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    minHeight: 150,
};

const timerDisplayStyle: React.CSSProperties = {
    fontSize: 48,
    fontWeight: "bold",
    color: "#ef4444",
    textAlign: "center",
    fontFamily: "monospace",
    letterSpacing: 2,
};

const timerButtonsStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    justifyContent: "center",
};

const timerButtonStyle: React.CSSProperties = {
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 4,
    border: "1px solid rgba(255,255,255,0.2)",
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "#ddd",
    cursor: "pointer",
    transition: "all 0.2s",
};

const timerButtonActiveStyle: React.CSSProperties = {
    ...timerButtonStyle,
    backgroundColor: "#ef4444",
    borderColor: "#ef4444",
    color: "white",
};

const lapItemStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "rgba(255,255,255,0.02)",
    borderRadius: 4,
    fontSize: 13,
    color: "#bbb",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
};

const lapsListStyle: React.CSSProperties = {
    maxHeight: 150,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 0,
};

const GroupAnnotator: React.FC = () => {
    // Magnifier constants
    const MAGNIFIER_SIZE = 200;
    const MAGNIFIER_ZOOM = 2;
    const { libraryId } = useParams<{ libraryId?: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [allSlices, setAllSlices] = useState<Slice[]>([]);
    const [slices, setSlices] = useState<Slice[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<Array<{ product: Product | null; offer: Offer | null }>>([]);
    const [paginatorHoverPrev, setPaginatorHoverPrev] = useState(false);
    const [paginatorHoverNext, setPaginatorHoverNext] = useState(false);
    const [showOnlyNoProduct, setShowOnlyNoProduct] = useState(false);
    const [jumpToPageInput, setJumpToPageInput] = useState<string>("");
    const [editingProductInPriceFirst, setEditingProductInPriceFirst] = useState<{ product: Product; offer: Offer; attributes?: any[] } | null>(null);
    const [editingBaseProduct, setEditingBaseProduct] = useState<{ product: Product | null; offer: Offer | null; attributes?: any[] } | null>(null);
    const [createdBaseProduct, setCreatedBaseProduct] = useState<{ product: Product; offer: Offer } | null>(null);
    const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0, imgWidth: 0, imgHeight: 0 });
    const [currentImage, setCurrentImage] = useState<any>(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [promotionsRefreshTrigger, setPromotionsRefreshTrigger] = useState(0);
    const [accordionOpen, setAccordionOpen] = useState<string | null>("priceFirst"); // "identifier" | "promotions" | "priceFirst" | "multiPriceFirst" | "groupProduct" | "baseProduct" | "offerGroups" | "extActionManager" | null
    const originalImageRef = useRef<HTMLImageElement>(null);
    const filteringRef = useRef(false);

    // Timer states
    const [timerRunning, setTimerRunning] = useState(false);
    const [timerElapsed, setTimerElapsed] = useState(0); // milliseconds
    const [timerLaps, setTimerLaps] = useState<number[]>([]);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Get index from search params, default to first slice (0)
    // This updates whenever the URL search params change
    const indexFromUrl = parseInt(searchParams.get('index') || '0', 10);

    const setIndex = (newIndex: number) => {
        setSearchParams({ index: newIndex.toString() });
    };

    const current = useMemo(() => slices[indexFromUrl], [slices, indexFromUrl]);

    const fetchSlices = useCallback(async () => {
        if (!libraryId) return;
        setLoading(true);
        try {
            const res = await fetch(`/api/admin/libraries/${libraryId}/slices`);
            if (!res.ok) {
                setAllSlices([]);
                setSlices([]);
                return;
            }
            const data = await res.json();
            const allSlicesData = (data.slices || []).reverse();
            setAllSlices(allSlicesData);
            // Don't filter here - let the separate effect handle filtering
            setSlices(allSlicesData);
        } catch (err) {
            setAllSlices([]);
            setSlices([]);
        } finally {
            setLoading(false);
        }
    }, [libraryId]);

    const filterSlicesWithoutProducts = async (slicesToFilter: Slice[], libId: string) => {
        try {
            // Lekérjük az összes azonosított terméket az library összes slicejához
            const res = await fetch(`/api/admin/libraries/${libId}/slices`);
            const data = await res.json();
            const allSlicesData = data.slices || [];

            // Lekérjük az összes ProductIdentifier-t - PARALLEL requests
            const identifiedSliceIds = new Set<number>();
            const promises = allSlicesData.map((slice: Slice) =>
                fetch(`/api/admin/product_identifiers/by_slice/${slice.id}`)
                    .then(res => res.json())
                    .then(idData => {
                        if (idData.success && idData.products && idData.products.length > 0) {
                            identifiedSliceIds.add(slice.id);
                        }
                    })
                    .catch(err => console.error(`Error fetching products for slice ${slice.id}:`, err))
            );

            // Megvárjuk az összes párhuzamos kérést
            await Promise.all(promises);

            // Csak azokat adjuk vissza, amelyekhez nincs termék
            return slicesToFilter.filter(slice => !identifiedSliceIds.has(slice.id));
        } catch (err) {
            console.error("Error filtering slices:", err);
            return slicesToFilter;
        }
    };

    useEffect(() => {
        fetchSlices();
    }, [fetchSlices]);

    // Apply filtering when showOnlyNoProduct toggle changes
    useEffect(() => {
        if (allSlices.length === 0) return;

        const applyFilter = async () => {
            filteringRef.current = true;
            try {
                if (showOnlyNoProduct) {
                    const filteredSlices = await filterSlicesWithoutProducts(allSlices, libraryId || "");
                    setSlices(filteredSlices);
                } else {
                    setSlices(allSlices);
                }
                // Don't reset index - preserve the URL parameter
            } finally {
                filteringRef.current = false;
            }
        };

        applyFilter();
    }, [showOnlyNoProduct, allSlices, libraryId]);

    // Ensure index is valid when slices change
    useEffect(() => {
        if (slices.length > 0 && indexFromUrl >= slices.length) {
            setIndex(0);
        }
    }, [slices.length, indexFromUrl]);

    useEffect(() => {
        if (current?.image_id) {
            setImageLoading(true);
            fetch(`/api/admin/images/${current.image_id}`)
                .then(res => res.json())
                .then(data => setCurrentImage(data.image))
                .catch(err => console.error(err))
                .finally(() => setImageLoading(false));
        }
    }, [current?.image_id]);

    const goPrev = () => {
        const newIndex = Math.max(0, indexFromUrl - 1);
        setIndex(newIndex);
    };

    const goNext = () => {
        const newIndex = Math.min(slices.length - 1, indexFromUrl + 1);
        setIndex(newIndex);
    };

    const goLast = () => {
        const newIndex = slices.length - 1;
        setIndex(newIndex);
    };

    const getPaginationNumbers = () => {
        const currentPage = indexFromUrl + 1;
        const totalPages = slices.length;
        const maxVisible = 5;
        const pages: (number | string)[] = [];

        if (totalPages <= maxVisible + 2) {
            // Ha kevés az oldal, akkor mindegyiket megmutatjuk
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Első oldal mindig
            pages.push(1);

            // Kiszámítjuk az aktuális oldal körüli terjedelmét
            const halfVisible = Math.floor(maxVisible / 2);
            let startPage = Math.max(2, currentPage - halfVisible);
            let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

            // Ha túl közel vagyunk az elejéhez vagy végéhez, kiterjesztjük
            if (currentPage <= halfVisible + 1) {
                endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
            }
            if (currentPage >= totalPages - halfVisible) {
                startPage = Math.max(2, endPage - maxVisible + 1);
            }

            // Ellipszis az első oldal után
            if (startPage > 2) {
                pages.push("...");
            }

            // Közös oldal számok
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            // Ellipszis az utolsó oldal előtt
            if (endPage < totalPages - 1) {
                pages.push("...");
            }

            // Utolsó oldal
            pages.push(totalPages);
        }

        return pages;
    };

    const handleJumpToPage = () => {
        const pageNum = parseInt(jumpToPageInput, 10);
        if (pageNum >= 1 && pageNum <= slices.length) {
            setIndex(pageNum - 1);
            setJumpToPageInput("");
        } else {
            alert(`Kérlek írj be egy szám 1 és ${slices.length} között`);
        }
    };

    const getUrl = (slice: Slice) => slice.slice_url || slice.url || slice.image_url || '';

    const handleDeleteProduct = async (index: number) => {
        const product = selectedProducts[index];
        if (!product?.offer?.id) {
            return;
        }

        if (!window.confirm("Biztosan szeretnéd törölni ezt a terméket?")) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/product_identifiers/${product.offer.id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();
            if (data.success) {
                setSelectedProducts(prev => prev.filter((_, i) => i !== index));
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            }
        } catch (error) {
            console.error("Error deleting product:", error);
        }
    };

    const handleCreateFamily = async (selectedIndices: number[]) => {
        if (!current?.id || selectedIndices.length < 2) {
            alert("Legalább 2 terméket kell hozzáadni a családba!");
            return;
        }

        try {
            // Get offer IDs from SELECTED products only
            const offerIds = selectedIndices
                .map(idx => selectedProducts[idx]?.offer?.id)
                .filter(id => id !== undefined && id !== null);

            if (offerIds.length < 2) {
                alert("Legalább 2 ajánlat szükséges a család létrehozásához!");
                return;
            }

            const response = await fetch('/api/admin/product_identifiers/create_family', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    slice_id: current.id,
                    offer_ids: offerIds
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the products to get updated family status
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            } else {
                alert(`Hiba: ${data.error}`);
            }
        } catch (error) {
            console.error("Error creating family:", error);
            alert("Hiba történt a család létrehozása során!");
        }
    };

    const handleRemoveFromFamily = async (offerId: number, familyGroupId: string | number) => {
        try {
            const response = await fetch('/api/admin/product_identifiers/update_family', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    offer_id: offerId,
                    action_type: 'remove_member',
                    family_group_id: familyGroupId
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the products to get updated family status
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            } else {
                alert(`Hiba: ${data.error}`);
            }
        } catch (error) {
            console.error("Error removing from family:", error);
            alert("Hiba történt a családból való eltávolítás során!");
        }
    };

    const handleRemoveFamily = async (familyGroupId?: string) => {
        if (!current?.id) return;

        if (!window.confirm("Biztos, hogy fel szeretnéd bontani a családot?")) {
            return;
        }

        try {
            const response = await fetch('/api/admin/product_identifiers/remove_family', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    slice_id: current.id,
                    family_group_id: familyGroupId
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the products to get updated family status
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            } else {
                alert(`Hiba: ${data.error}`);
            }
        } catch (error) {
            console.error("Error removing family:", error);
            alert("Hiba történt a család felbontása során!");
        }
    };

    const handleCreateGroup = async (selectedIndices: number[]) => {
        if (!current?.id || selectedIndices.length < 1) {
            alert("Legalább 1 terméket kell hozzáadni a csoportba!");
            return;
        }

        try {
            // Get offer IDs from SELECTED products only
            const offerIds = selectedIndices
                .map(idx => selectedProducts[idx]?.offer?.id)
                .filter(id => id !== undefined && id !== null);

            if (offerIds.length < 1) {
                alert("Legalább 1 ajánlat szükséges a csoport létrehozásához!");
                return;
            }

            const response = await fetch('/api/admin/product_identifiers/create_group', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    slice_id: current.id,
                    offer_ids: offerIds
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the products to get updated group status
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            } else {
                alert(`Hiba: ${data.error}`);
            }
        } catch (error) {
            console.error("Error creating group:", error);
            alert("Hiba történt a csoport létrehozása során!");
        }
    };

    const handleRemoveFromGroup = async (offerId: number, groupGroupId: string) => {
        try {
            const response = await fetch('/api/admin/product_identifiers/update_group', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    offer_id: offerId,
                    action_type: 'remove_member',
                    group_group_id: groupGroupId
                })
            });

            const data = await response.json();
            if (data.success) {
                // Refresh the products to get updated group status
                if (current?.id) {
                    fetchIdentifiedProducts(current.id);
                }
            } else {
                alert(`Hiba: ${data.error}`);
            }
        } catch (error) {
            console.error("Error removing from group:", error);
            alert("Hiba történt a csoportból való eltávolítás során!");
        }
    };

    const fetchIdentifiedProducts = useCallback(async (sliceId: number) => {
        try {
            const res = await fetch(`/api/admin/product_identifiers/by_slice/${sliceId}`);
            const data = await res.json();
            if (data.success && data.products) {
                setSelectedProducts(data.products.map((item: any) => ({
                    product: item.product,
                    offer: item.offer,
                    attributes: item.attributes || []
                })));
            } else {
                setSelectedProducts([]);
            }
        } catch (err) {
            console.error("Error fetching identified products:", err);
            setSelectedProducts([]);
        }
    }, []);

    useEffect(() => {
        if (current?.id) {
            fetchIdentifiedProducts(current.id);
        }
    }, [current?.id, fetchIdentifiedProducts]);

    // Timer effect
    useEffect(() => {
        if (timerRunning) {
            timerIntervalRef.current = setInterval(() => {
                setTimerElapsed(prev => prev + 10);
            }, 10);
        } else {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }

        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, [timerRunning]);

    // Timer helper functions
    const formatTime = (ms: number) => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const centiseconds = Math.floor((ms % 1000) / 10);
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
    };

    const handleTimerStart = () => {
        setTimerRunning(true);
    };

    const handleTimerStop = () => {
        setTimerRunning(false);
    };

    const handleTimerLap = () => {
        if (timerRunning) {
            setTimerLaps(prev => [...prev, timerElapsed]);
            setTimerElapsed(0);
        }
    };

    const handleTimerReset = () => {
        setTimerRunning(false);
        setTimerElapsed(0);
        setTimerLaps([]);
    };

    return (
        <div style={pageStyle}>
            {/* Paginator bar at the top - full width */}
            {slices.length > 0 && (
                <div style={paginatorBarStyle}>
                    <div style={{ fontSize: 13, color: "#ddd", fontWeight: 500 }}>Kép</div>
                    <button
                        onClick={() => {
                            if (filteringRef.current) return;
                            setShowOnlyNoProduct(!showOnlyNoProduct);
                        }}
                        style={showOnlyNoProduct ? toggleActiveStyle : toggleStyle}
                        title="Csak azokat a képeket mutassa, amelyekhez nincs még termék hozzárendelve"
                    >
                        <div style={showOnlyNoProduct ? toggleSwitchActiveStyle : toggleSwitchStyle}>
                            <div style={showOnlyNoProduct ? toggleDotActiveStyle : toggleDotStyle} />
                        </div>
                        Nincs termék
                    </button>

                    <div style={paginatorStyle}>
                        <button
                            onClick={goPrev}
                            onMouseEnter={() => setPaginatorHoverPrev(true)}
                            onMouseLeave={() => setPaginatorHoverPrev(false)}
                            aria-label="Első oldal"
                            style={paginatorHoverPrev ? paginatorButtonHoverStyle : paginatorButtonStyle}
                        >
                            ≪ Első
                        </button>
                        <button
                            onClick={goPrev}
                            onMouseEnter={() => setPaginatorHoverPrev(true)}
                            onMouseLeave={() => setPaginatorHoverPrev(false)}
                            aria-label="Előző"
                            style={paginatorHoverPrev ? paginatorButtonHoverStyle : paginatorButtonStyle}
                        >
                            ◀ Előző
                        </button>

                        <div style={paginatorNumbersContainerStyle}>
                            {getPaginationNumbers().map((page, idx) => (
                                page === "..." ? (
                                    <div key={`ellipsis-${idx}`} style={paginatorEllipsisStyle}>
                                        …
                                    </div>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => setIndex((page as number) - 1)}
                                        style={
                                            page === indexFromUrl + 1
                                                ? paginatorNumberActiveStyle
                                                : paginatorNumberStyle
                                        }
                                        onMouseEnter={(e) => {
                                            if (page !== indexFromUrl + 1) {
                                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (page !== indexFromUrl + 1) {
                                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                                            }
                                        }}
                                    >
                                        {page}
                                    </button>
                                )
                            ))}
                        </div>

                        <button
                            onClick={goNext}
                            onMouseEnter={() => setPaginatorHoverNext(true)}
                            onMouseLeave={() => setPaginatorHoverNext(false)}
                            aria-label="Következő"
                            style={paginatorHoverNext ? paginatorButtonHoverStyle : paginatorButtonStyle}
                        >
                            Következő ▶
                        </button>
                        <button
                            onClick={goLast}
                            onMouseEnter={() => setPaginatorHoverNext(true)}
                            onMouseLeave={() => setPaginatorHoverNext(false)}
                            aria-label="Utolsó oldal"
                            style={paginatorHoverNext ? paginatorButtonHoverStyle : paginatorButtonStyle}
                        >
                            Utolsó ≫
                        </button>
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                            type="number"
                            value={jumpToPageInput}
                            onChange={(e) => setJumpToPageInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    handleJumpToPage();
                                }
                            }}
                            placeholder="Oldal"
                            min="1"
                            max={slices.length}
                            style={paginatorInputStyle}
                            title={`Add meg az oldal számot (1-${slices.length})`}
                        />
                        <button
                            onClick={handleJumpToPage}
                            style={{
                                padding: "6px 12px",
                                fontSize: 12,
                                color: "#ddd",
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: 4,
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                            }}
                        >
                            Ugrás
                        </button>
                    </div>

                    <button
                        onClick={() => {
                            const page = searchParams.get('page');
                            if (page) {
                                navigate(`/discounts?page=${page}`);
                            } else {
                                navigate('/discounts');
                            }
                        }}
                        style={{
                            padding: "6px 12px",
                            fontSize: 13,
                            color: "#ddd",
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 4,
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                        }}
                    >
                        ← Mégse
                    </button>
                </div>
            )}

            {/* Grid with 4 columns */}
            <div style={gridStyle}>
                <div style={firstColumnStyle}>
                    {/* Image container - Slice */}
                    <div style={{ ...imageWrapperStyle, position: "relative", flexDirection: "column" }}>
                        {loading ? (
                            <div style={placeholderStyle}>Betöltés…</div>
                        ) : current ? (
                            <>
                                <img src={getUrl(current)} alt={`Slice ${current.id}`} style={imageStyle} onClick={goNext} />
                                <div style={{ position: "absolute", bottom: 8, left: 8, color: "#666", fontSize: 11, backgroundColor: "rgba(0,0,0,0.5)", padding: "4px 8px", borderRadius: 3 }}>
                                    Slice ID: {current.id}
                                </div>
                            </>
                        ) : (
                            <div style={placeholderStyle}>Nincsenek képek ebben a mappában.</div>
                        )}
                    </div>

                    {/* Original Image container */}
                    <div style={{
                        ...halfColumnStyle,
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        borderTop: "1px solid rgba(255,255,255,0.06)",
                        position: "relative"
                    }}
                        onMouseMove={(e) => {
                            if (!originalImageRef.current) return;
                            const img = originalImageRef.current;
                            const rect = img.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const y = e.clientY - rect.top;
                            // Csak ha az egér a kép területén van
                            if (x < 0 || x > rect.width || y < 0 || y > rect.height) {
                                return;
                            }
                            // Store pixel coords and image size for precise background positioning
                            setMagnifierPos({ x, y, imgWidth: rect.width, imgHeight: rect.height });
                        }}
                        onMouseLeave={() => {
                            setMagnifierPos({ x: 0, y: 0, imgWidth: 0, imgHeight: 0 });
                        }}
                        onMouseEnter={() => {/* removed */ }}
                    >
                        <div style={labelStyle}>Eredeti kép</div>
                        {imageLoading ? (
                            <div style={placeholderStyle}>Betöltés…</div>
                        ) : currentImage?.image_url ? (
                            <>
                                <img
                                    src={currentImage.image_url}
                                    alt={`Original ${currentImage.id}`}
                                    style={{
                                        ...imageStyle,
                                        cursor: "crosshair",
                                    }}
                                    ref={originalImageRef}
                                />
                                {/* Magnifying glass panel */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "10px",
                                        left: "10px",
                                        width: MAGNIFIER_SIZE,
                                        height: MAGNIFIER_SIZE,
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        zIndex: 1000,
                                        pointerEvents: "none",
                                        backgroundImage: `url(${currentImage.image_url})`,
                                        backgroundRepeat: "no-repeat",
                                        // Use pixel-based background size so the zoom matches the original image
                                        backgroundSize: `${magnifierPos.imgWidth * MAGNIFIER_ZOOM}px ${magnifierPos.imgHeight * MAGNIFIER_ZOOM}px`,
                                        // Position so the cursor-centered point is shown in the center of the magnifier
                                        backgroundPosition: `${-(magnifierPos.x * MAGNIFIER_ZOOM - MAGNIFIER_SIZE / 2)}px ${-(magnifierPos.y * MAGNIFIER_ZOOM - MAGNIFIER_SIZE / 2)}px`,
                                        backgroundColor: "rgba(0, 0, 0, 0.9)",
                                        border: "2px solid rgba(255, 255, 255, 0.3)",
                                    }}
                                />
                            </>
                        ) : (
                            <div style={placeholderStyle}>Nincs eredeti kép</div>
                        )}
                    </div>
                </div>

                <div style={{ ...columnBase, position: "relative" }}>
                    <div style={labelStyle}>Összefoglalás</div>
                    <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                        <GroupAnnotatorDetails
                            products={selectedProducts}
                            onDeleteProduct={handleDeleteProduct}
                            currentSliceId={current?.id}
                            onRefresh={() => fetchIdentifiedProducts(current?.id || 0)}
                            onCreateFamily={handleCreateFamily}
                            onRemoveFamily={handleRemoveFamily}
                            onRemoveFromFamily={handleRemoveFromFamily}
                            onCreateGroup={handleCreateGroup}
                            onRemoveFromGroup={handleRemoveFromGroup}
                            promotionsRefreshTrigger={promotionsRefreshTrigger}
                            onEditProduct={(product: any, offer: any, attributes: any[] = []) => {
                                if (product && offer) {
                                    const fullProduct: Product = {
                                        id: product.id,
                                        name: product.name,
                                        category_id: product.category_id || 0,
                                        category: product.category || "",
                                        brand_id: product.brand_id || null,
                                        brand_name: product.brand_name || null,
                                        ean_code: product.ean_code || null,
                                    };
                                    // GroupAnnotatorDetails shows identified products, so edit in price-first panel
                                    setEditingProductInPriceFirst({ product: fullProduct, offer, attributes });
                                    setAccordionOpen("priceFirst");
                                }
                            }}
                        />
                    </Suspense>
                </div>

                {/* Accordion Container - Ár-elsőbbségű keresés, Termékazonosító, Kategória Akciók */}
                <div style={accordionContainerStyle}>
                    {/* Panel 1: Ár-elsőbbségű keresés (starts open) */}
                    <div style={accordionOpen === "priceFirst" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "priceFirst" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "priceFirst" ? null : "priceFirst")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "priceFirst") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "priceFirst") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "priceFirst" ? "▼" : "▶"}</span>
                            💰 Ár-elsőbbségű keresés
                        </button>
                        {accordionOpen === "priceFirst" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorPriceFirstProduct
                                        current={current ? { id: current.id } : null}
                                        currentImageId={current?.image_id}
                                        currentSliceId={current?.id}
                                        preloadedProduct={editingProductInPriceFirst || createdBaseProduct}
                                        onOfferSelected={() => {
                                            if (current?.id) {
                                                fetchIdentifiedProducts(current.id);
                                            }
                                            setEditingProductInPriceFirst(null);
                                            setCreatedBaseProduct(null);
                                        }}
                                        onClosePanel={() => setAccordionOpen(null)}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 1.5: Multi-Árfelvevő */}
                    <div style={accordionOpen === "multiPriceFetcher" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "multiPriceFetcher" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "multiPriceFetcher" ? null : "multiPriceFetcher")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "multiPriceFetcher") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "multiPriceFetcher") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "multiPriceFetcher" ? "▼" : "▶"}</span>
                            💸 Multi-Árfelvevő
                        </button>
                        {accordionOpen === "multiPriceFetcher" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorMultiPriceFetcher
                                        current={current ? { id: current.id } : null}
                                        currentImageId={current?.image_id}
                                        currentSliceId={current?.id}
                                        onClosePanel={() => setAccordionOpen(null)}
                                        onSave={() => fetchIdentifiedProducts(current?.id || 0)}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 2: Multi Ár-elsőbbségű keresés */}
                    <div style={accordionOpen === "multiPriceFirst" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "multiPriceFirst" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "multiPriceFirst" ? null : "multiPriceFirst")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "multiPriceFirst") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "multiPriceFirst") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "multiPriceFirst" ? "▼" : "▶"}</span>
                            � Multi-Alaptermék felvevő
                        </button>
                        {accordionOpen === "multiPriceFirst" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorMultiPriceFirstProduct
                                        current={current ? { id: current.id } : null}
                                        currentImageId={current?.image_id}
                                        currentSliceId={current?.id}
                                        onProductsCreated={() => {
                                            if (current?.id) {
                                                fetchIdentifiedProducts(current.id);
                                            }
                                            setAccordionOpen("priceFirst");
                                        }}
                                        onClosePanel={() => setAccordionOpen(null)}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel: Csoportos Termék Felvevő */}
                    <div style={accordionOpen === "groupProduct" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "groupProduct" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "groupProduct" ? null : "groupProduct")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "groupProduct") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "groupProduct") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "groupProduct" ? "▼" : "▶"}</span>
                            👥 Csoportos Termék felvevő
                        </button>
                        {accordionOpen === "groupProduct" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorGroupProduct
                                        current={current ? { id: current.id } : null}
                                        currentImageId={current?.image_id}
                                        currentSliceId={current?.id}
                                        onProductsCreated={() => {
                                            if (current?.id) {
                                                fetchIdentifiedProducts(current.id);
                                            }
                                            setAccordionOpen("priceFirst");
                                        }}
                                        onClosePanel={() => setAccordionOpen(null)}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 2: Alap termék szerkesztése */}
                    <div style={accordionOpen === "baseProduct" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "baseProduct" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "baseProduct" ? null : "baseProduct")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "baseProduct") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "baseProduct") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "baseProduct" ? "▼" : "▶"}</span>
                            📝 Alap termék szerkesztése
                        </button>
                        {accordionOpen === "baseProduct" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorBaseProduct
                                        current={current ? { id: current.id } : null}
                                        preloadedProduct={editingBaseProduct}
                                        onProductSelected={(product: any) => {
                                            // Handle product selection/creation
                                            if (current?.id) {
                                                fetchIdentifiedProducts(current.id);
                                            }
                                            // Set the product as created from base panel - this is SEPARATE from ár-elsőbbségű keresés
                                            if (product) {
                                                const fullProduct: Product = {
                                                    id: product.id,
                                                    name: product.name,
                                                    category_id: product.category_id || 0,
                                                    category: product.category || "",
                                                    brand_id: product.brand_id || null,
                                                    brand_name: product.brand_name || null,
                                                    ean_code: product.ean_code || null,
                                                };
                                                const defaultOffer: Offer = {
                                                    id: 0,
                                                    currency: "HUF",
                                                    min_quantity: 1,
                                                    x: 0,
                                                    y: 0,
                                                    width: null,
                                                    height: null,
                                                    packaging: null,
                                                };
                                                // Store in createdBaseProduct, NOT in editingProductInPriceFirst
                                                setCreatedBaseProduct({ product: fullProduct, offer: defaultOffer });
                                                // Clear editingBaseProduct
                                                setEditingBaseProduct(null);
                                            }
                                        }}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 2.5: Ext-Akció-Kezelő */}
                    <div style={accordionOpen === "extActionManager" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "extActionManager" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "extActionManager" ? null : "extActionManager")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "extActionManager") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "extActionManager") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "extActionManager" ? "▼" : "▶"}</span>
                            🔗 Ext-Akció-Kezelő
                        </button>
                        {accordionOpen === "extActionManager" && (
                            <div style={accordionContentStyle}>
                                <ExtActionManager
                                    onActionSelected={(action, product) => {
                                        // Árak konvertálása: regular (eredeti) és promotional (akciós)
                                        const regularPrice = action.prices_data?.find(p => p.price_type_code === 'regular');
                                        const promotionalPrice = action.prices_data?.find(p => p.price_type_code === 'promotional');

                                        // Csomagolás konvertálása: "Je" -> "db"
                                        let sizeUnit = action.packaging_data?.size_unit || "db";
                                        if (!action.packaging_data?.size_unit && action.packaging_data?.name?.toLowerCase() === 'je') {
                                            sizeUnit = "db";
                                        }

                                        // Árak konvertálása az Offer.prices formátumára
                                        const offerPrices: any[] = [];
                                        if (regularPrice) {
                                            offerPrices.push({
                                                id: action.id,
                                                price_type: {
                                                    id: 1,
                                                    name: "Eredeti ár",
                                                    code: "regular"
                                                },
                                                value: regularPrice.value,
                                                starts_at: null,
                                                ends_at: null
                                            });
                                        }
                                        if (promotionalPrice) {
                                            offerPrices.push({
                                                id: action.id + 1,
                                                price_type: {
                                                    id: 2,
                                                    name: "Akciós ár",
                                                    code: "promotional"
                                                },
                                                value: promotionalPrice.value,
                                                starts_at: action.promotion_data?.starts_at ?
                                                    new Date((typeof action.promotion_data.starts_at === 'number' ? action.promotion_data.starts_at * 1000 : action.promotion_data.starts_at)).toISOString().split('T')[0] : null,
                                                ends_at: action.promotion_data?.ends_at ?
                                                    new Date((typeof action.promotion_data.ends_at === 'number' ? action.promotion_data.ends_at * 1000 : action.promotion_data.ends_at)).toISOString().split('T')[0] : null
                                            });
                                        }

                                        // Csomagolás konvertálása az Offer.packaging formátumára
                                        const offerPackaging = action.packaging_data?.size_value ? {
                                            id: action.id,
                                            name: action.packaging_data.name || "db",
                                            size_value: action.packaging_data.size_value,
                                            size_unit: sizeUnit,
                                            multiplier: action.packaging_data.multiplier || 1
                                        } : null;

                                        // Akció betöltése az ExtPromotions adataiból
                                        setEditingProductInPriceFirst({
                                            product: product as Product,
                                            offer: {
                                                id: action.id,
                                                currency: action.promotion_data?.currency || "EUR",
                                                min_quantity: 1,
                                                x: 0,
                                                y: 0,
                                                width: null,
                                                height: null,
                                                prices: offerPrices.length > 0 ? offerPrices : undefined,
                                                packaging: offerPackaging
                                            },
                                            attributes: []
                                        });

                                        // Nyitott az árkeresés panelt
                                        setAccordionOpen("priceFirst");
                                    }}
                                    onCancel={() => setAccordionOpen(null)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Panel 3: Termékazonosító */}
                    <div style={accordionOpen === "identifier" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "identifier" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "identifier" ? null : "identifier")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "identifier") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "identifier") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "identifier" ? "▼" : "▶"}</span>
                            Termékazonosító
                        </button>
                        {accordionOpen === "identifier" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorProductIdentifier
                                        libraryId={libraryId}
                                        current={current ? { id: current.id, image_id: current.image_id, slice_id: current.id, shop_id: currentImage?.shop_id } : null}
                                        onSave={() => fetchIdentifiedProducts(current?.id || 0)}
                                        onProductSelected={(product: any, offer: any) => {
                                            if (product && offer) {
                                                const fullProduct: Product = {
                                                    id: product.id,
                                                    name: product.name,
                                                    category_id: product.category_id || 0,
                                                    category: product.category || "",
                                                    brand_id: product.brand_id || null,
                                                    brand_name: product.brand_name || null,
                                                    ean_code: product.ean_code || null,
                                                };
                                                const fullOffer: Offer = {
                                                    id: offer.id || 0,
                                                    currency: offer.currency || "HUF",
                                                    min_quantity: offer.min_quantity || 1,
                                                    x: offer.x || 0,
                                                    y: offer.y || 0,
                                                    width: offer.width || null,
                                                    height: offer.height || null,
                                                    packaging: offer.packaging || null,
                                                };
                                                // Pass to price-first panel for editing
                                                setEditingProductInPriceFirst({ product: fullProduct, offer: fullOffer });
                                                // Automatically open the price/packaging panel for editing
                                                setAccordionOpen("priceFirst");
                                            }
                                        }}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 4: Kategória Akciók */}
                    <div style={accordionOpen === "promotions" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "promotions" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "promotions" ? null : "promotions")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "promotions") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "promotions") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "promotions" ? "▼" : "▶"}</span>
                            🎯 Kategória Akciók
                        </button>
                        {accordionOpen === "promotions" && (
                            <div style={accordionContentStyle}>
                                {currentImage?.shop_id && current?.id ? (
                                    <div style={{ width: "100%", height: "100%", overflowY: "auto", padding: "12px 8px" }}>
                                        <CategoryPromotionManager
                                            shopId={currentImage.shop_id}
                                            sliceId={current.id}
                                            onRefresh={() => setPromotionsRefreshTrigger(prev => prev + 1)}
                                        />
                                    </div>
                                ) : (
                                    <div style={{ padding: "15px", color: "#888", fontSize: "12px", textAlign: "center" }}>
                                        ⚠️ Bolt és slice szükséges
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Panel 5: Termékcsomagolók */}
                    <div style={accordionOpen === "offerGroups" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "offerGroups" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "offerGroups" ? null : "offerGroups")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "offerGroups") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "offerGroups") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "offerGroups" ? "▼" : "▶"}</span>
                            📦 Termékcsomagolók
                        </button>
                        {accordionOpen === "offerGroups" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <ProductOfferGroupManager
                                        sliceId={current?.id}
                                        onRefresh={() => fetchIdentifiedProducts(current?.id || 0)}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>

                    {/* Panel 6: Autoloader */}
                    <div style={accordionOpen === "autoloader" ? accordionPanelOpenStyle : accordionPanelStyle}>
                        <button
                            style={{
                                ...accordionHeaderStyle,
                                backgroundColor: accordionOpen === "autoloader" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                            }}
                            onClick={() => setAccordionOpen(accordionOpen === "autoloader" ? null : "autoloader")}
                            onMouseEnter={(e) => {
                                if (accordionOpen !== "autoloader") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (accordionOpen !== "autoloader") {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)";
                                }
                            }}
                        >
                            <span>{accordionOpen === "autoloader" ? "▼" : "▶"}</span>
                            🤖 Autoloader
                        </button>
                        {accordionOpen === "autoloader" && (
                            <div style={accordionContentStyle}>
                                <Suspense fallback={<div style={placeholderStyle}>Betöltés…</div>}>
                                    <GroupAnnotatorAutoloader
                                        sliceId={current?.id}
                                        onRefresh={() => fetchIdentifiedProducts(current?.id || 0)}
                                        onScraperSuccess={() => setAccordionOpen("priceFirst")}
                                    />
                                </Suspense>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Timer Section */}
            <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 }}>
                <div style={timerContainerStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#ddd" }}>⏱️ Timer</div>
                        <div style={{ fontSize: 11, color: "#888" }}>
                            {timerLaps.length} kör
                        </div>
                    </div>

                    <div style={timerDisplayStyle}>
                        {formatTime(timerElapsed)}
                    </div>

                    <div style={timerButtonsStyle}>
                        {!timerRunning ? (
                            <button
                                style={timerButtonActiveStyle}
                                onClick={handleTimerStart}
                            >
                                ▶ Start
                            </button>
                        ) : (
                            <button
                                style={timerButtonActiveStyle}
                                onClick={handleTimerStop}
                            >
                                ⏸ Stop
                            </button>
                        )}

                        <button
                            style={timerButtonStyle}
                            onClick={handleTimerLap}
                            disabled={!timerRunning}
                            onMouseEnter={(e) => {
                                if (timerRunning) {
                                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                            }}
                        >
                            🔄 Kör
                        </button>

                        <button
                            style={timerButtonStyle}
                            onClick={handleTimerReset}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.1)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                            }}
                        >
                            🔄 Reset
                        </button>
                    </div>

                    {timerLaps.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <div style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>Körök:</div>
                            <div style={lapsListStyle}>
                                {timerLaps.map((lap, index) => (
                                    <div key={index} style={lapItemStyle}>
                                        <span>Kör {index + 1}:</span>
                                        <span style={{ fontFamily: "monospace", color: "#ef4444" }}>
                                            {formatTime(lap)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );

};

export default GroupAnnotator;

